import { AsyncPipe, NgComponentOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, ExperimentalPendingTasks, inject, Injector } from '@angular/core';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { DynamicComponentWithInjector } from 'src/app/core/classes/DynamicComponent';
import { BuilderPage } from 'src/app/core/interfaces';
import { createPageComponentService } from 'src/app/shared/factories/page-component-service.factory';
import { createInjector } from 'src/app/shared/helpers/page-component.helper';
import { PAGE_DATA } from 'src/types/tokens/inject-tokens';

@Component({
  selector: 'app-default',
  templateUrl: './default.component.html',
  styles: `
    :host {
      display: block;
    }
  `,
  standalone: true,
  imports: [NgComponentOutlet, AsyncPipe],
})
export class AppPageDefaultComponent {
  private pageData: BuilderPage | null = inject(PAGE_DATA, { optional: true });
  components: Promise<DynamicComponentWithInjector[]>;

  private changeDetector = inject(ChangeDetectorRef);
  private pendingTasks = inject(ExperimentalPendingTasks);
  private logger = inject(GenericLogger);
  private injector = inject(Injector);

  constructor() {
    const componentsReadyTask = this.pendingTasks.add();
    const customComponents = this.pageData?.extraData?.components?.customComponents;
    if (customComponents) {
      this.logger.info('Default component: Loading custom components', customComponents);
      this.components = this.#createDynamicComponents(customComponents);
    } else {
      this.logger.info('Default component No custom components found');
      this.components = Promise.resolve([]);
    }
    this.components
      .then(() => {
        this.changeDetector.detectChanges();
        this.logger.info('Default component Dynamic Components loaded');
      })
      .finally(() => {
        this.logger.debug('Default component SSR dynamic components ready');
        componentsReadyTask();
      });
  }

  async #createDynamicComponents(componentsMeta: any[]): Promise<DynamicComponentWithInjector[]> {
    const componentsPack = await Promise.all(
      componentsMeta.map(async (componentMeta) => {
        const pageComponentService = createPageComponentService(this.injector, componentMeta.component);
        const component = await pageComponentService.getPageComponent(componentMeta.component);

        if (component === null) {
          this.logger.error('Default component Error loading dynamic component', componentMeta);
        }
        const injector = createInjector(this.injector, componentMeta.component.options);
        return { component, injector };
      }),
    );
    return componentsPack.filter<DynamicComponentWithInjector>((di): di is DynamicComponentWithInjector => di.component !== null);
  }
}
